import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShieldCheck, CreditCard, Smartphone, Landmark, Wallet, MessageCircle, Lock, AlertTriangle } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ProductImage from "@/components/ProductImage";
import { useCart, formatINR } from "@/context/CartContext";
import { useMetaPixel, firePurchaseOnce } from "@/context/MetaPixelContext";
import { useAnalytics } from "@/context/AnalyticsContext";
import PhoneOtpVerifier from "@/components/PhoneOtpVerifier";
import { DeliveryLocationPicker, useGoogleMapsConfig } from "@/components/DeliveryLocationPicker";
import usePageTitle from "@/hooks/usePageTitle";
import useWhatsAppOrderEnabled from "@/hooks/useWhatsAppOrderEnabled";
import { startCashfreeCheckout } from "@/lib/cashfree";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RZP_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const PAY_METHODS = [
  { id: "razorpay-upi", label: "UPI", icon: Smartphone, method: "razorpay", note: "GPay · PhonePe · Paytm · Any UPI" },
  { id: "razorpay-card", label: "Credit / Debit Card", icon: CreditCard, method: "razorpay", note: "Visa · Mastercard · RuPay · Amex" },
  { id: "razorpay-nb", label: "Net Banking", icon: Landmark, method: "razorpay", note: "All major Indian banks" },
  { id: "razorpay-wallet", label: "Wallets", icon: Wallet, method: "razorpay", note: "Paytm · Mobikwik · Amazon Pay" },
  { id: "whatsapp", label: "WhatsApp Assisted Order", icon: MessageCircle, method: "whatsapp", note: "Support channel — prepaid link sent by team" },
];

/** Strip spaces, +91, leading 0 and special chars → clean 10-digit Indian mobile, or null. Mirrors backend phone_utils. */
export const normalizeIndianMobile = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d.length === 10 && /^[6-9]/.test(d) ? d : null;
};
const PHONE_ERROR = "Please enter a valid 10-digit Indian mobile number (e.g. 98765 43210).";


const CheckoutPage = () => {
  usePageTitle("Secure Checkout");
  const nav = useNavigate();
  const { items, rawItems, subtotal, totalShipped, clear } = useCart();
  const pixel = useMetaPixel();
  const analytics = useAnalytics();
  const [payMethod, setPayMethod] = useState("razorpay-upi");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "",
    address_line1: "", address_line2: "", city: "", state: "", pincode: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [otpVerifiedPhone, setOtpVerifiedPhone] = useState("");
  const [pendingOrder, setPendingOrder] = useState(null);  // last created 'pending' order — reused on retry
  const whatsappEnabled = useWhatsAppOrderEnabled() === true; // Admin › General Settings "WhatsApp Order Button"
  const [gatewayInfo, setGatewayInfo] = useState(null); // { active, active_enabled, gateways } from /settings/payment
  useEffect(() => {
    axios.get(`${API}/settings/payment`).then((r) => setGatewayInfo(r.data)).catch(() => setGatewayInfo(null));
  }, []);
  const gatewayName = gatewayInfo?.gateways?.[gatewayInfo.active]?.display_name
    || (gatewayInfo?.active === "cashfree" ? "Cashfree" : "Razorpay");
  const onlineDisabled = gatewayInfo ? gatewayInfo.active_enabled === false : false;
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount, description }
  const [couponBusy, setCouponBusy] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false); // mandatory legal consent
  const [termsError, setTermsError] = useState(false);
  const discount = appliedCoupon?.discount || 0;
  const payableTotal = Math.max(0, subtotal - discount);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCouponBusy(true);
    try {
      const r = await axios.get(`${API}/coupons/validate`, { params: { code, subtotal } });
      setAppliedCoupon({ code: r.data.code, discount: r.data.discount, description: r.data.description });
      toast.success(`Coupon ${r.data.code} applied — you save ${formatINR(r.data.discount)}`);
    } catch (e) {
      setAppliedCoupon(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon code");
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };
  const mapsCfg = useGoogleMapsConfig();
  const [deliveryLoc, setDeliveryLoc] = useState(null);    // optional Google Maps delivery location
  const [pinWarn, setPinWarn] = useState("");              // pincode serviceability warning (non-blocking)

  // Live pincode serviceability check (warn-only — never blocks checkout)
  useEffect(() => {
    const pin = (form.pincode || "").trim();
    if (!/^\d{6}$/.test(pin)) { setPinWarn(""); return; }
    const t = setTimeout(async () => {
      try {
        await axios.get(`${API}/serviceability`, { params: { pincode: pin } });
        setPinWarn("");
      } catch {
        setPinWarn("Heads up — this pincode may be outside our delivery zones. You can still place the order; we'll confirm availability on WhatsApp before shipping.");
      }
    }, 500);
    return () => clearTimeout(t);
  }, [form.pincode]);

  // Load OTP feature flag once — read from the master `verification_required`
  // signal, NOT the raw `enabled` flag. The backend computes this from
  // (enabled AND enabled_live AND provider fully configured). So MSG91 or any
  // future provider (Twilio, Firebase) automatically activates checkout OTP
  // the moment credentials are saved and Go-Live is toggled.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/otp`);
        setOtpEnabled(!!r.data?.verification_required);
      } catch {
        setOtpEnabled(false);
      }
    })();
  }, []);

  // Payment methods shown to the customer — WhatsApp option hides when admin turns it off
  const visiblePayMethods = PAY_METHODS.filter((m) => m.method !== "whatsapp" || whatsappEnabled);

  useEffect(() => {
    // Only redirect if the raw cart (localStorage-backed) is truly empty.
    // `items` (hydrated) can transiently be empty while ProductsContext loads.
    if ((rawItems || []).length === 0) {
      const t = setTimeout(() => nav("/cart"), 200);
      return () => clearTimeout(t);
    }
  }, [rawItems, nav]);

  // Fire InitiateCheckout when the page is loaded with a valid cart
  useEffect(() => {
    if (items.length === 0) return;
    pixel.track("InitiateCheckout", {
      content_ids: items.map((it) => it.slug),
      contents: items.map((it) => ({ id: it.slug, quantity: it.qty, item_price: it.product?.price ? it.product.price / 100 : undefined })),
      num_items: items.reduce((s, it) => s + it.qty, 0),
      value: subtotal / 100,
      currency: "INR",
    });
    analytics.fireCheckoutStarted(items.map((it) => ({ slug: it.slug, name: it.product?.name, qty: it.qty })), subtotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const on = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.customer_name.trim()) errs.customer_name = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Valid email required";
    if (!normalizeIndianMobile(form.phone)) errs.phone = PHONE_ERROR;
    if (!form.address_line1.trim()) errs.address_line1 = "Required";
    if (!form.city.trim()) errs.city = "Required";
    if (!form.state.trim()) errs.state = "Required";
    if (!/^\d{6}$/.test(form.pincode.trim())) errs.pincode = "6-digit pincode";
    if (otpEnabled) {
      const verified = !!otpToken && otpVerifiedPhone && (otpVerifiedPhone.endsWith(form.phone.replace(/\D/g, "")) || form.phone.replace(/\D/g, "").endsWith(otpVerifiedPhone.slice(-10)));
      if (!verified) errs.phone_otp = "Please verify your phone via OTP";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const isPhoneVerified = () => {
    if (!otpEnabled) return true;
    if (!otpToken) return false;
    const digits = form.phone.replace(/\D/g, "");
    if (!digits || !otpVerifiedPhone) return false;
    return otpVerifiedPhone.endsWith(digits) || digits.endsWith(otpVerifiedPhone.slice(-10));
  };

  const buildPayload = () => {
    const method = visiblePayMethods.find((m) => m.id === payMethod) || visiblePayMethods[0];
    return {
      customer_name: form.customer_name.trim(),
      email: form.email.trim(),
      phone: normalizeIndianMobile(form.phone) || form.phone.trim(),
      address_line1: form.address_line1.trim(),
      address_line2: (form.address_line2 || "").trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      payment_method: method.method,
      notes: form.notes.trim(),
      otp_verify_token: otpToken || undefined,
      session_id: analytics.getSessionId ? analytics.getSessionId() : undefined,
      // Optional Google Maps delivery location — saved with the order for Admin/delivery
      latitude: deliveryLoc?.latitude ?? undefined,
      longitude: deliveryLoc?.longitude ?? undefined,
      google_maps_url: deliveryLoc?.google_maps_url || undefined,
      location_source: deliveryLoc?.location_source || undefined,
      items: items.map((it) => ({
        slug: it.slug, name: it.product.name, qty: it.qty,
        unit_price: it.product.price, shipped_units: it.shippedUnits, is_bogo: it.isBogo,
      })),
      subtotal, shipping: 0, total: payableTotal,
      coupon_code: appliedCoupon?.code || undefined,
      terms_accepted: termsAccepted,
    };
  };

  const openRazorpay = async (order) => {
    const ok = await loadRazorpay();
    if (!ok) throw new Error("Payment SDK failed to load. Please refresh and try again.");
    const rzp = await axios.post(`${API}/orders/${order.id}/razorpay/create`);
    // Set the moment Razorpay hands us a signed success response. Guards the
    // dismiss / payment.failed paths below from cancelling an order whose
    // payment actually succeeded (Razorpay can fire ondismiss right after
    // the handler on some UPI / redirect flows).
    let paymentDone = false;
    const opts = {
      key: rzp.data.key_id,
      amount: rzp.data.amount,
      currency: rzp.data.currency,
      name: "HerbKranti",
      description: "Sea Buckthorn Pure Pulp",
      order_id: rzp.data.razorpay_order_id,
      theme: { color: "#D97535" },
      prefill: { name: order.customer_name, email: order.email, contact: order.phone },
      handler: async (resp) => {
        paymentDone = true;
        try {
          await axios.post(`${API}/orders/verify-payment`, {
            order_id: order.id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          // Only after backend verification succeeds: fire Purchase (deduped),
          // clear cart, and route to the success page.
          firePurchaseOnce(order.id, {
            value: (order.total || 0) / 100,
            currency: "INR",
            content_ids: (order.items || []).map((i) => i.slug),
            contents: (order.items || []).map((i) => ({ id: i.slug, quantity: i.qty, item_price: i.unit_price ? i.unit_price / 100 : undefined })),
            num_items: (order.items || []).reduce((s, i) => s + (i.qty || 0), 0),
            order_id: order.id,
          });
          clear();
          setPendingOrder(null);
          nav(`/order/${order.id}`);
        } catch (e) {
          // Verification failed — DO NOT clear cart, DO NOT navigate to a success page.
          // Release stock reservation so the customer can retry.
          try { await axios.post(`${API}/orders/${order.id}/cancel`); } catch {}
          setPendingOrder(null);
          toast.error("Payment could not be verified. Your cart is intact — please try again.");
          setBusy(false);
        }
      },
      modal: {
        ondismiss: async () => {
          // Razorpay can fire dismiss right after a successful handler on some
          // UPI / redirect flows — never cancel an order whose payment succeeded.
          if (paymentDone) return;
          // Customer closed / cancelled the Razorpay window.
          // Keep cart intact, release reservation, stay on checkout for retry.
          try { await axios.post(`${API}/orders/${order.id}/cancel`); } catch {}
          setPendingOrder(null);
          toast.message("Payment cancelled — your items are still in your cart. Try again whenever you're ready.");
          setBusy(false);
        },
      },
    };
    const inst = new window.Razorpay(opts);
    inst.on && inst.on("payment.failed", async (evt) => {
      if (paymentDone) return; // success handler already ran — never cancel a paid order
      try { await axios.post(`${API}/orders/${order.id}/cancel`); } catch {}
      setPendingOrder(null);
      const reason = evt?.error?.description || "Payment failed";
      toast.error(`${reason} — your cart is intact, please try again.`);
      setBusy(false);
    });
    inst.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (!termsAccepted) {
      setTermsError(true);
      toast.error("Please accept the Privacy Policy and Terms of Use to continue.");
      return;
    }
    setBusy(true);
    try {
      const payload = buildPayload();
      const res = await axios.post(`${API}/orders`, payload);
      const order = res.data;
      setPendingOrder(order);
      const method = visiblePayMethods.find((m) => m.id === payMethod) || visiblePayMethods[0];
      if (method.method === "razorpay") {
        try {
          // Branch on the gateway the backend resolved from Admin › Payment Gateways (saved active gateway).
          if (order.payment_gateway === "cashfree") {
            await startCashfreeCheckout(order);
            // Cashfree redirects to its hosted page and returns to /order/:id?cf_return=1
            return;
          }
          await openRazorpay(order);
          // openRazorpay's callbacks handle busy=false + navigation.
          return;
        } catch (e) {
          // Payment SDK didn't even open — cancel the pending order.
          try { await axios.post(`${API}/orders/${order.id}/cancel`); } catch {}
          setPendingOrder(null);
          const detail = e?.response?.data?.detail || e.message;
          toast.error(detail || "Payment gateway not available. Please try again in a moment.");
        }
      } else if (method.method === "whatsapp") {
        // WhatsApp is a support channel only — team sends a prepaid payment link.
        // DO NOT clear the cart — order is not paid until the customer completes the link.
        const msg = encodeURIComponent(
          `Hi HerbKranti, I've just placed order ${order.id.slice(0, 8)} for ${order.items.map((i) => `${i.qty} × ${i.name}`).join(", ")}. Please share the prepaid payment link.`
        );
        window.open(`https://wa.me/917249797972?text=${msg}`, "_blank");
        nav(`/order/${order.id}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      if (typeof detail === "string" && /mobile number/i.test(detail)) {
        setErrors((prev) => ({ ...prev, phone: detail }));
        setTimeout(() => document.querySelector("[data-testid='checkout-error-phone']")?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      }
      toast.error(detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if ((rawItems || []).length === 0) return null;

  return (
    <>
      <section className="relative pt-16 pb-8 lg:pt-24 lg:pb-12 overflow-hidden">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Link to="/cart" className="inline-flex items-center gap-2 text-[13px] tracking-widest uppercase text-[var(--hk-ink-2)] hover:text-[var(--hk-orange-deep)]" data-testid="checkout-back-btn">
            <ArrowLeft size={14} /> Back to Cart
          </Link>
          <div className="mt-8">
            <div className="label-eyebrow">Checkout</div>
            <h1 className="serif mt-3 text-[42px] sm:text-[52px] leading-[1.05] text-[var(--hk-ink)]" data-testid="checkout-title">
              Complete your <span className="italic-accent text-[var(--hk-orange-deep)]">ritual.</span>
            </h1>
          </div>
        </div>
      </section>

      <form onSubmit={submit} noValidate className="pb-24" data-testid="checkout-form">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          {/* LEFT: form */}
          <div className="lg:col-span-7 space-y-10">
            {/* Details */}
            <div className="hk-card p-8">
              <h2 className="serif text-[22px] text-[var(--hk-ink)]">Customer Details</h2>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <Field label="Full Name" id="customer_name" value={form.customer_name} onChange={on("customer_name")} error={errors.customer_name} placeholder="Your full name" />
                <div className="space-y-1.5">
                  <PhoneOtpVerifier
                    phone={form.phone}
                    onPhoneChange={(v) => {
                      setForm((f) => ({ ...f, phone: v }));
                      setErrors((prev) => ({ ...prev, phone: undefined, phone_otp: undefined }));
                      // Editing after verify resets verification
                      if (otpToken) { setOtpToken(null); setOtpVerifiedPhone(""); }
                    }}
                    verified={isPhoneVerified()}
                    onVerified={(token, normalizedPhone) => {
                      setOtpToken(token);
                      setOtpVerifiedPhone(normalizedPhone || form.phone.replace(/\D/g, ""));
                      setErrors((prev) => ({ ...prev, phone_otp: undefined }));
                    }}
                    enabled={otpEnabled}
                  />
                  {errors.phone && <p className="text-xs text-red-600" data-testid="checkout-error-phone">{errors.phone}</p>}
                  {errors.phone_otp && <p className="text-xs text-red-600" data-testid="checkout-error-phone-otp">{errors.phone_otp}</p>}
                </div>
                <Field label="Email" id="email" type="email" value={form.email} onChange={on("email")} error={errors.email} placeholder="you@example.com" className="sm:col-span-2" />
              </div>
            </div>

            {/* Address */}
            <div className="hk-card p-8">
              <h2 className="serif text-[22px] text-[var(--hk-ink)]">Delivery Address</h2>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <Field className="sm:col-span-2" label="Address Line 1" id="address_line1" value={form.address_line1} onChange={on("address_line1")} error={errors.address_line1} placeholder="Flat / house / building, street" />
                <Field className="sm:col-span-2" label="Address Line 2 (optional)" id="address_line2" value={form.address_line2} onChange={on("address_line2")} placeholder="Landmark, area" />
                <Field label="City" id="city" value={form.city} onChange={on("city")} error={errors.city} />
                <Field label="State" id="state" value={form.state} onChange={on("state")} error={errors.state} />
                <div>
                  <Field label="Pincode" id="pincode" value={form.pincode} onChange={on("pincode")} error={errors.pincode} placeholder="6-digit pincode" />
                  {pinWarn && (
                    <p className="mt-1.5 text-[12px] text-amber-700 flex items-start gap-1.5" data-testid="checkout-pincode-warning">
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {pinWarn}
                    </p>
                  )}
                </div>
                <Field label="Notes for delivery (optional)" id="notes" value={form.notes} onChange={on("notes")} className="sm:col-span-2" />
              </div>
            </div>

            {/* Google Maps delivery location (only renders when Admin enables the integration) */}
            <DeliveryLocationPicker
              config={mapsCfg}
              value={deliveryLoc}
              onChange={setDeliveryLoc}
              onAddressParts={(parts) => setForm((f) => ({
                ...f,
                address_line1: parts.address_line1 || f.address_line1,
                city: parts.city || f.city,
                state: parts.state || f.state,
                pincode: parts.pincode || f.pincode,
              }))}
              testid="checkout-maps"
            />

            {/* Payment method */}
            <div className="hk-card p-8">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <h2 className="serif text-[22px] text-[var(--hk-ink)]">Payment Method</h2>
                <span
                  className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.28em] uppercase px-3 py-1.5 rounded-full bg-[var(--hk-orange)]/10 text-[var(--hk-orange-deep)] border border-[var(--hk-orange)]/25"
                  data-testid="checkout-prepaid-badge"
                >
                  <Lock size={11} strokeWidth={2} /> Prepaid Orders Only
                </span>
              </div>
              <p className="mt-4 text-[13px] leading-[1.75] text-[var(--hk-ink-2)]" data-testid="checkout-prepaid-note">
                To ensure faster processing and secure order confirmation, all HerbKranti orders
                are accepted through prepaid payment methods only. Cash on Delivery is not
                available.
              </p>
              {onlineDisabled && (
                <div className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-[13px] text-red-700" data-testid="checkout-gateway-disabled">
                  Online payments via {gatewayName} are currently disabled by the store. Please try again later{whatsappEnabled ? " or order via WhatsApp below" : ""}.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3 mt-6" data-testid="checkout-payment-methods">
                {visiblePayMethods.map((m) => {
                  const Icon = m.icon;
                  const active = payMethod === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setPayMethod(m.id)}
                      data-testid={`checkout-pay-${m.id}`}
                      className={
                        "flex items-start gap-3 p-4 rounded-[12px] border text-left transition-colors " +
                        (active
                          ? "border-[var(--hk-orange)] bg-[var(--hk-warm-ivory)]"
                          : "border-[var(--hk-border)] bg-white hover:border-[var(--hk-orange)]")
                      }
                    >
                      <span className={"mt-0.5 w-8 h-8 rounded-full flex items-center justify-center border " + (active ? "border-[var(--hk-orange)] text-[var(--hk-orange-deep)]" : "border-[var(--hk-border)] text-[var(--hk-ink-2)]")}>
                        <Icon size={15} strokeWidth={1.6} />
                      </span>
                      <span className="flex-1">
                        <span className="block text-[14px] font-medium text-[var(--hk-ink)]">{m.label}</span>
                        <span className="block text-[11.5px] text-[var(--hk-ink-3)] mt-0.5 leading-relaxed">{m.note}</span>
                      </span>
                      <span className={"mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 " + (active ? "border-[var(--hk-orange)] bg-[var(--hk-orange)]" : "border-[var(--hk-border)]")} />
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex items-center gap-3 text-[12px] text-[var(--hk-ink-3)]">
                <ShieldCheck size={13} className="text-[var(--hk-orange-deep)]" /> All online payments processed securely via {gatewayName}.
              </div>
            </div>
          </div>

          {/* RIGHT: summary sticky */}
          <div className="lg:col-span-5">
            <div className="hk-card p-8 lg:sticky lg:top-24" data-testid="checkout-summary">
              <div className="label-eyebrow">Order Summary</div>
              <ul className="mt-5 space-y-4">
                {items.map((it) => (
                  <li key={it.slug} className="flex items-center gap-4" data-testid={`checkout-line-${it.slug}`}>
                    <div className="w-16 h-16 rounded-[10px] overflow-hidden bg-[var(--hk-warm-ivory)] shrink-0">
                      <ProductImage product={it.product} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="serif text-[15px] text-[var(--hk-ink)] truncate">{it.product.name}</div>
                      <div className="text-[12px] text-[var(--hk-ink-3)] mt-0.5">Qty {it.qty} · Ships {it.shippedUnits} {it.isBogo && `(${it.product.offer})`}</div>
                    </div>
                    <div className="serif text-[16px] text-[var(--hk-ink)]">{formatINR(it.linePrice)}</div>
                  </li>
                ))}
              </ul>
              {/* Coupon code */}
              <div className="mt-6 pt-6 border-t border-[var(--hk-border)]">
                {appliedCoupon ? (
                  <div className="flex items-center justify-between gap-3 rounded-md bg-[var(--hk-green-soft,#eef5ee)] border border-[var(--hk-green,#3a7d44)]/30 px-3 py-2.5" data-testid="coupon-applied">
                    <div className="min-w-0">
                      <span className="font-mono text-[13px] font-semibold text-[var(--hk-green,#3a7d44)]">{appliedCoupon.code}</span>
                      <span className="text-[12px] text-[var(--hk-ink-3)] ml-2">−{formatINR(appliedCoupon.discount)}</span>
                    </div>
                    <button type="button" onClick={removeCoupon} className="text-[11px] text-[var(--hk-ink-3)] hover:text-red-600 transition-colors shrink-0" data-testid="coupon-remove-btn">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="Coupon code"
                      className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] h-10 rounded-md font-mono text-[13px] uppercase"
                      data-testid="coupon-input"
                    />
                    <button type="button" onClick={applyCoupon} disabled={couponBusy || !couponInput.trim()}
                      className="hk-btn-ghost h-10 px-4 text-[12.5px] shrink-0 inline-flex items-center gap-1.5 disabled:opacity-60"
                      data-testid="coupon-apply-btn">
                      {couponBusy && <Loader2 size={13} className="animate-spin" />} Apply
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-[var(--hk-border)] space-y-2 text-[14px] text-[var(--hk-ink-2)]">
                <div className="flex justify-between"><span>Subtotal</span><span className="text-[var(--hk-ink)]">{formatINR(subtotal)}</span></div>
                <div className="flex justify-between"><span>Shipping</span><span className="text-[var(--hk-ink)]">Free</span></div>
                {appliedCoupon && (
                  <div className="flex justify-between text-[var(--hk-green,#3a7d44)]" data-testid="checkout-discount-row">
                    <span>Discount ({appliedCoupon.code})</span><span>−{formatINR(discount)}</span>
                  </div>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-[var(--hk-border)] flex justify-between items-center">
                <span className="label-eyebrow">Total</span>
                <span className="serif text-[26px] text-[var(--hk-ink)]" data-testid="checkout-total">{formatINR(payableTotal)}</span>
              </div>
              <label
                className={"mt-6 flex items-start gap-3 rounded-lg border p-3.5 cursor-pointer transition-colors " +
                  (termsError && !termsAccepted ? "border-red-400 bg-red-50/60" : "border-[var(--hk-border)] bg-white/70 hover:border-[var(--hk-ink)]/40")}
                data-testid="checkout-consent-label"
              >
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) setTermsError(false); }}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-[var(--hk-orange-deep)]"
                  data-testid="checkout-consent-checkbox"
                />
                <span className="text-[12.5px] leading-[1.7] text-[var(--hk-ink-2)]">
                  I have read and agree to HerbKranti's{" "}
                  <Link to="/privacy-policy" target="_blank" rel="noreferrer" className="text-[var(--hk-orange-deep)] underline underline-offset-2 hover:text-[var(--hk-ink)]" data-testid="checkout-privacy-link">Privacy Policy</Link>
                  {" "}and{" "}
                  <Link to="/terms-and-conditions" target="_blank" rel="noreferrer" className="text-[var(--hk-orange-deep)] underline underline-offset-2 hover:text-[var(--hk-ink)]" data-testid="checkout-terms-link">Terms of Use</Link>.
                  <span className="text-red-600"> *</span>
                </span>
              </label>
              {termsError && !termsAccepted && (
                <p className="mt-1.5 text-xs text-red-600" data-testid="checkout-consent-error">Please accept to place your order.</p>
              )}
              <button type="submit" disabled={busy || !termsAccepted || (otpEnabled && !isPhoneVerified())} className="hk-btn-primary w-full justify-center mt-4 disabled:opacity-70" data-testid="checkout-place-order-btn">
                {busy && <Loader2 size={16} className="animate-spin" />}
                {busy ? "Processing" : (otpEnabled && !isPhoneVerified() ? "Verify Phone to Continue" : "Place Order")}
              </button>
              <div className="mt-4 flex items-center gap-2 text-[11px] text-[var(--hk-ink-3)] justify-center">
                <LeafGlyph className="w-3 h-3 text-[var(--hk-gold-deep)]" /> Thoughtfully packed. Beautifully delivered.
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

const Field = ({ label, id, className = "", error, type = "text", ...rest }) => (
  <div className={"space-y-1.5 " + className}>
    <Label htmlFor={id} className="text-[11px] tracking-[0.18em] uppercase text-[var(--hk-ink-3)]">{label}</Label>
    <Input id={id} type={type} data-testid={`checkout-field-${id}`} className="bg-white border-[var(--hk-border)] focus-visible:ring-[var(--hk-orange)] h-11 rounded-md" {...rest} />
    {error && <p className="text-xs text-red-600" data-testid={`checkout-error-${id}`}>{error}</p>}
  </div>
);

export default CheckoutPage;
